import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Gavel, Send, AlertTriangle, Clock, ArrowUpRight,
  FileSearch, BarChart3, Layers, CheckCircle2, Trophy
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const CHART_COLORS = ['#18181B', '#3B82F6', '#52525B', '#A1A1AA', '#D4D4D8', '#F59E0B', '#10B981', '#EF4444'];
const STATUS_COLOR_MAP = {
  DRAFT: '#A1A1AA', OPEN: '#3B82F6', CLOSED: '#52525B',
  EVALUATION: '#F59E0B', AWARDED: '#10B981', CANCELLED: '#EF4444',
};
const TYPE_COLOR_MAP = {
  RFI: '#6366F1', RFQ: '#3B82F6', RFP: '#10B981', REVERSE_AUCTION: '#F59E0B', OTHER: '#A1A1AA',
};

export default function RFxDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/rfx/dashboard/summary')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load sourcing dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const drillTo = (params) => {
    const sp = new URLSearchParams({ tab: 'events', ...params });
    navigate(`/rfq?${sp.toString()}`);
  };
  const goEvent = (ref) => navigate(`/rfq?edit=${ref}`);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-6 w-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full" />
    </div>
  );
  if (!data) return <div className="text-center py-16 text-zinc-400">Dashboard unavailable</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="rfx-dashboard">
      <KPIStrip kpis={k} drillTo={drillTo} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ClosingSoon events={data.closing_soon} recentlyClosed={data.recently_closed} goEvent={goEvent} drillTo={drillTo} />
        </div>
        <StatusBreakdown data={data.by_status} drillTo={drillTo} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TypeBreakdown data={data.by_type} pipeline={data.pipeline} drillTo={drillTo} />
        <EventTrend data={data.trend} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CategoryBreakdown data={data.by_category} drillTo={drillTo} />
        <SourcingPipeline pipeline={data.pipeline} drillTo={drillTo} />
      </div>
    </div>
  );
}

/* ────────────────────── KPI STRIP ────────────────────── */
function KPIStrip({ kpis: k, drillTo }) {
  const cards = [
    { label: 'Total Events', value: k.total_count, icon: FileSearch, testid: 'kpi-total-events',
      onClick: () => drillTo({ label: 'All Events' }) },
    { label: 'Open', value: k.open_count, icon: Send, testid: 'kpi-open',
      warn: false,
      onClick: () => drillTo({ status: 'OPEN', label: 'Open Events' }) },
    { label: 'In Evaluation', value: k.evaluation_count, icon: BarChart3, testid: 'kpi-evaluation',
      onClick: () => drillTo({ status: 'EVALUATION', label: 'In Evaluation' }) },
    { label: 'Awarded', value: k.awarded_count, icon: Trophy, testid: 'kpi-awarded',
      onClick: () => drillTo({ status: 'AWARDED', label: 'Awarded' }) },
    { label: 'Suppliers Invited', value: k.total_invited, icon: Layers, testid: 'kpi-invited',
      sub: `${k.avg_suppliers} avg/event`, subColor: 'text-zinc-500',
      onClick: () => drillTo({ label: 'All Events' }) },
    { label: 'Responses', value: k.total_responses, icon: Gavel, testid: 'kpi-responses',
      onClick: () => drillTo({ label: 'All Events' }) },
  ];

  return (
    <div className="flex flex-col md:flex-row flex-wrap lg:flex-nowrap border border-zinc-200 rounded-md overflow-hidden shadow-sm" data-testid="rfx-kpi-strip">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} onClick={c.onClick}
            className={`flex-1 min-w-[140px] p-4 cursor-pointer transition-all hover:bg-zinc-50 active:bg-zinc-100 group ${i > 0 ? 'border-t md:border-t-0 md:border-l' : ''} border-zinc-200 bg-white`}
            data-testid={c.testid}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${c.warn ? 'text-red-500' : 'text-zinc-400'}`} strokeWidth={1.5} />
                <span className="text-[11px] font-semibold tracking-wide uppercase text-zinc-500">{c.label}</span>
              </div>
              <ArrowUpRight className="h-3 w-3 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className={`text-xl font-semibold tracking-tight ${c.warn ? 'text-red-600' : 'text-zinc-900'}`}>{c.value}</p>
            {c.sub && <p className={`text-[11px] mt-0.5 ${c.subColor || 'text-zinc-400'}`}>{c.sub}</p>}
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────── CLOSING SOON + RECENTLY CLOSED ────────────────────── */
function ClosingSoon({ events, recentlyClosed, goEvent, drillTo }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="closing-soon">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Sourcing Activity</h3>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Closing Within 7 Days
            <Badge className="bg-red-100 text-red-700 text-[9px] ml-1">{events.length}</Badge>
          </p>
          {events.length === 0 ? <p className="text-xs text-zinc-400 italic">No events closing soon</p> : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {events.map(e => (
                <div key={e.event_ref}
                  className="flex items-center justify-between text-xs px-3 py-2 rounded hover:bg-zinc-50 cursor-pointer transition-colors"
                  onClick={() => goEvent(e.event_ref)}
                  data-testid={`closing-${e.event_ref}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className="bg-zinc-100 text-zinc-600 text-[9px] shrink-0">{e.type}</Badge>
                    <span className="font-mono text-primary font-semibold">{e.event_ref}</span>
                  </div>
                  <span className="text-zinc-500 truncate mx-2 max-w-[180px]">{e.title}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-zinc-400 text-[10px]">{e.supplier_count} suppliers</span>
                    <Badge className={`text-[9px] ${e.days_left <= 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {e.days_left}d left
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {recentlyClosed.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Recently Closed / Awarded
              <Badge className="bg-zinc-100 text-zinc-600 text-[9px] ml-1">{recentlyClosed.length}</Badge>
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {recentlyClosed.map(e => (
                <div key={e.event_ref}
                  className="flex items-center justify-between text-xs px-3 py-2 rounded hover:bg-zinc-50 cursor-pointer transition-colors"
                  onClick={() => goEvent(e.event_ref)}
                  data-testid={`recent-${e.event_ref}`}>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-zinc-100 text-zinc-600 text-[9px]">{e.type}</Badge>
                    <span className="font-mono text-primary font-medium">{e.event_ref}</span>
                  </div>
                  <span className="text-zinc-500 truncate mx-2 max-w-[200px]">{e.title}</span>
                  <Badge className={`text-[9px] ${e.status === 'AWARDED' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>{e.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────── STATUS BREAKDOWN (Donut) ────────────────────── */
function StatusBreakdown({ data, drillTo }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  const chartData = data.map(d => ({ ...d, fill: STATUS_COLOR_MAP[d.status] || '#D4D4D8' }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="rfx-status-breakdown">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Event Status</h3>
      </div>
      <div className="p-5 flex flex-col items-center">
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={chartData} dataKey="count" nameKey="status" cx="50%" cy="50%"
              innerRadius={50} outerRadius={75} strokeWidth={2} stroke="#fff"
              onClick={(entry) => entry && drillTo({ status: entry.status, label: `Status: ${entry.status}` })}
              cursor="pointer">
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={(val, name) => [`${val} events`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="text-center -mt-4 mb-3">
          <p className="text-2xl font-bold text-zinc-900">{total}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Total Events</p>
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
          {chartData.map(d => (
            <div key={d.status}
              className="flex items-center gap-1.5 text-xs cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => drillTo({ status: d.status, label: `Status: ${d.status}` })}
              data-testid={`rfx-status-${d.status}`}>
              <div className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />
              <span className="text-zinc-600">{d.status}</span>
              <span className="font-semibold text-zinc-900">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── TYPE BREAKDOWN + PIPELINE ────────────────────── */
function TypeBreakdown({ data, pipeline, drillTo }) {
  const chartData = data.map(d => ({
    ...d, name: d.type, fill: TYPE_COLOR_MAP[d.type] || '#A1A1AA'
  }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="rfx-type-breakdown">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Events by Type</h3>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={85}
              strokeWidth={2} stroke="#fff"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}
              onClick={(entry) => entry && drillTo({ type: entry.name, label: `Type: ${entry.name}` })}
              cursor="pointer">
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={(val, name) => [`${val} events`, name]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── EVENT TREND (Line) ────────────────────── */
function EventTrend({ data }) {
  const chartData = data.map(d => ({ ...d, label: d.month.slice(5) }));
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="rfx-trend">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Events Created Over Time</h3>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wide">Last 12 months</span>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip formatter={(v) => [`${v} events`, 'Created']} />
            <Line type="monotone" dataKey="count" stroke="#18181B" strokeWidth={2} dot={{ r: 3, fill: '#18181B' }} activeDot={{ r: 6, cursor: 'pointer' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── CATEGORY BREAKDOWN ────────────────────── */
function CategoryBreakdown({ data, drillTo }) {
  const chartData = data.map(d => ({
    ...d,
    name: d.category.length > 20 ? d.category.slice(0, 18) + '...' : d.category,
    fullName: d.category
  }));

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="rfx-categories">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Events by Category</h3>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#52525b' }} width={130} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => [`${v} events`, 'Count']} />
            <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} barSize={16}
              onClick={(entry) => entry && entry.fullName && drillTo({ search: entry.fullName, label: `Category: ${entry.fullName}` })}
              cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── SOURCING PIPELINE (Active by type) ────────────────────── */
function SourcingPipeline({ pipeline, drillTo }) {
  const total = pipeline.reduce((s, p) => s + p.count, 0);

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="sourcing-pipeline">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Active Sourcing Pipeline</h3>
        <p className="text-[10px] text-zinc-400 mt-0.5">{total} open events by type</p>
      </div>
      <div className="p-5">
        {pipeline.length === 0 ? (
          <p className="text-xs text-zinc-400 italic text-center py-8">No active sourcing events</p>
        ) : (
          <div className="space-y-3">
            {pipeline.map((p, i) => {
              const maxCount = Math.max(...pipeline.map(x => x.count), 1);
              const pct = Math.round((p.count / maxCount) * 100);
              const color = TYPE_COLOR_MAP[p.type] || CHART_COLORS[i % CHART_COLORS.length];
              return (
                <div key={p.type}
                  className="flex items-center gap-3 cursor-pointer group"
                  onClick={() => drillTo({ type: p.type, status: 'OPEN', label: `Open ${p.type}` })}
                  data-testid={`pipeline-${p.type}`}>
                  <span className="text-xs text-zinc-600 w-36 shrink-0 text-right font-medium group-hover:text-primary group-hover:underline transition-colors">
                    {p.type.replace(/_/g, ' ')}
                  </span>
                  <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden">
                    <div className="h-full rounded transition-all duration-500 group-hover:opacity-80 flex items-center px-2"
                      style={{ width: `${pct}%`, background: color }}>
                      <span className="text-[10px] font-bold text-white">{p.count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
